import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, Form, Input, Button, message, Space, Select, Tag, Input as AntInput } from 'antd';
import { SaveOutlined, ArrowLeftOutlined, HomeOutlined } from '@ant-design/icons';
import RichTextEditor from '../../components/RichTextEditor';
import VideoUploader from '../../components/knowledgeBase/VideoUploader';
import AttachmentUploader from '../../components/knowledgeBase/AttachmentUploader';
import { knowledgeBaseService, KnowledgeBaseFormData, VideoInfo, Attachment } from '../../services/knowledgeBase/knowledgeBaseService';
import { PRESET_CATEGORIES, PRESET_TAGS } from './presets';
import '../MemberManagement.css';
import '../../styles/resourceManagement.css';

const { TextArea } = Input;
const { Option } = Select;

type TeamUserOption = {
  _id: string;
  name?: string;
  username: string;
  role?: string;
};

function normalizeAllowedUserIds(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((u) => {
      if (typeof u === 'string' && u.trim()) return u.trim();
      if (u && typeof u === 'object' && '_id' in u) {
        const id = (u as { _id: string })._id;
        return id ? String(id) : '';
      }
      return '';
    })
    .filter(Boolean);
}

const KnowledgeBaseForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const accessLevel = Form.useWatch('accessLevel', form);
  const [loading, setLoading] = useState(false);
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [video, setVideo] = useState<VideoInfo | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [teamUsers, setTeamUsers] = useState<TeamUserOption[]>([]);
  const [teamUsersLoading, setTeamUsersLoading] = useState(true);
  const isEdit = !!id;

  useEffect(() => {
    const fetchUsers = async () => {
      setTeamUsersLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch('/api/auth/users', {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        const userData = await res.json();
        if (Array.isArray(userData)) {
          setTeamUsers(userData.filter((u: TeamUserOption) => u.role !== '超级管理员'));
        }
      } catch {
        console.warn('获取成员列表失败');
      } finally {
        setTeamUsersLoading(false);
      }
    };
    fetchUsers();
  }, []);

  const fetchKnowledgeBase = useCallback(async () => {
    if (!id) return;
    try {
      const data = await knowledgeBaseService.getById(id);
      form.setFieldsValue({
        title: data.title,
        summary: data.summary,
        content: data.content,
        category: data.category || data.categoryId,
        accessLevel: data.accessLevel,
        status: data.status,
        allowedUsers: normalizeAllowedUserIds(data.allowedUsers),
      });
      setTags(data.tags || []);
      if (data.video && data.video.key) {
        setVideo(data.video);
      }
      if (data.attachments && data.attachments.length > 0) {
        setAttachments(data.attachments);
      }
    } catch (error: any) {
      message.error(error.message || '获取文档失败');
      navigate('/knowledge-base');
    }
  }, [id, form, navigate]);

  useEffect(() => {
    if (isEdit && id) {
      fetchKnowledgeBase();
    }
  }, [id, isEdit, fetchKnowledgeBase]);

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      const level = values.accessLevel || 'public';
      const allowed =
        level === 'restricted' ? (Array.isArray(values.allowedUsers) ? values.allowedUsers : []) : [];

      const formData: KnowledgeBaseFormData = {
        title: values.title,
        content: values.content,
        summary: values.summary,
        category: values.category,
        tags: tags,
        video: video || undefined,
        attachments: attachments.length > 0 ? attachments : undefined,
        accessLevel: level,
        allowedUsers: allowed,
        status: values.status || 'draft',
      };

      if (isEdit && id) {
        await knowledgeBaseService.update(id, formData);
        message.success('更新成功');
      } else {
        await knowledgeBaseService.create(formData);
        message.success('创建成功');
      }
      navigate('/knowledge-base');
    } catch (error: any) {
      message.error(error.message || '保存失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setTags(tags.filter((t) => t !== tag));
  };

  const handleSelectPresetTag = (tag: string) => {
    if (!tags.includes(tag)) {
      setTags([...tags, tag]);
    }
  };

  return (
    <div className="page-container full-height members-lims kb-lims">
      <nav className="mm-crumb" aria-label="breadcrumb">
        <HomeOutlined />
        <span>首页</span>
        <span className="mm-crumb-sep">/</span>
        <Link to="/knowledge-base" className="kb-crumb-link">
          知识库
        </Link>
        <span className="mm-crumb-sep">/</span>
        <span aria-current="page">{isEdit ? '编辑文档' : '新建文档'}</span>
      </nav>

      <header className="mm-page-head">
        <div>
          <h1 className="mm-title">{isEdit ? '编辑文档' : '新建文档'}</h1>
          <p className="mm-subtitle">维护标题、正文与附件，保存后与列表和详情页使用同一套实验室界面规范。</p>
        </div>
      </header>

      <div className="kb-form-panel">
        <Card bordered={false} className="kb-form-card">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              accessLevel: 'public',
              status: 'draft',
              allowedUsers: [],
            }}
          >
            <Form.Item name="title" label="标题" rules={[{ required: true, message: '请输入标题' }]}>
              <Input placeholder="请输入文档标题" />
            </Form.Item>

            <Form.Item name="summary" label="摘要">
              <TextArea rows={3} placeholder="请输入文档摘要（可选）" maxLength={500} showCount />
            </Form.Item>

            <Form.Item name="content" label="内容" rules={[{ required: true, message: '请输入内容' }]}>
              <RichTextEditor
                value={form.getFieldValue('content') || ''}
                onChange={(value) => form.setFieldsValue({ content: value })}
                height={400}
              />
            </Form.Item>

            <Form.Item label="视频">
              <VideoUploader value={video || undefined} onChange={(v) => setVideo(v)} />
            </Form.Item>

            <Form.Item label="附件">
              <AttachmentUploader value={attachments} onChange={(atts) => setAttachments(atts)} maxSize={100} maxCount={10} />
            </Form.Item>

            <Form.Item name="category" label="分类">
              <Select placeholder="选择分类（可选）" allowClear>
                {PRESET_CATEGORIES.map((cat) => (
                  <Option key={cat.value} value={cat.value}>
                    {cat.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="标签">
              <Space direction="vertical" style={{ width: '100%' }}>
                {PRESET_TAGS.length > 0 && (
                  <div>
                    <span className="kb-form-hint">预设标签（点击添加）：</span>
                    <Space wrap size={[4, 4]}>
                      {PRESET_TAGS.map((tag) => (
                        <Tag
                          key={tag}
                          style={{ cursor: 'pointer', marginBottom: 0 }}
                          color={tags.includes(tag) ? 'blue' : 'default'}
                          onClick={() => handleSelectPresetTag(tag)}
                        >
                          {tag}
                        </Tag>
                      ))}
                    </Space>
                  </div>
                )}
                <div>
                  <span className="kb-form-hint">已选标签：</span>
                  <Space wrap>
                    {tags.map((tag) => (
                      <Tag key={tag} closable onClose={() => handleRemoveTag(tag)} color="blue">
                        {tag}
                      </Tag>
                    ))}
                  </Space>
                </div>
                <Space>
                  <AntInput
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onPressEnter={handleAddTag}
                    placeholder="输入自定义标签后按回车添加"
                    style={{ width: 220 }}
                  />
                  <Button onClick={handleAddTag}>添加</Button>
                </Space>
              </Space>
            </Form.Item>

            <Form.Item name="accessLevel" label="访问权限">
              <Select>
                <Option value="public">公开（全员可见）</Option>
                <Option value="private">私有（仅本人）</Option>
                <Option value="restricted">受限访问（指定成员）</Option>
              </Select>
            </Form.Item>

            {accessLevel === 'restricted' ? (
              <Form.Item
                name="allowedUsers"
                label="可访问成员"
                rules={[
                  {
                    validator: (_, value) => {
                      const list = Array.isArray(value) ? value : [];
                      if (list.length === 0) {
                        return Promise.reject(new Error('受限访问须至少指定一名可访问成员；若仅需本人可见，请选择「私有」'));
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
                extra="创建者始终可访问。请从团队中选择其他可阅读本文档的成员；若仅需本人可见，请改用「私有」。"
              >
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  placeholder="搜索姓名或用户名，选择可访问的成员"
                  optionFilterProp="label"
                  loading={teamUsersLoading}
                  options={teamUsers.map((u) => ({
                    value: u._id,
                    label: `${(u.name || '').trim() || u.username} (${u.username})`
                  }))}
                />
              </Form.Item>
            ) : null}

            <Form.Item name="status" label="状态">
              <Select>
                <Option value="draft">草稿</Option>
                <Option value="published">已发布</Option>
                <Option value="archived">已归档</Option>
              </Select>
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading}>
                  {isEdit ? '更新' : '创建'}
                </Button>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/knowledge-base')}>
                  返回列表
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
      </div>
    </div>
  );
};

export default KnowledgeBaseForm;
